import Vue from 'vue';
import { Event, EventDate, ShopData } from '@openticket/lib-shop';

export interface TimeslotFilter {
    key: string;
    start: Date;
    end: Date;

    eventdates: EventDate[];
    events: Event[];
}

export interface TimeslotFilters {
    filters: { [key: string]: TimeslotFilter };
    sorted: TimeslotFilter[];
    selected: TimeslotFilter | null;
    update(data: ShopData, timeslotQuery?: string | null): void;
}

export function createFilters(): TimeslotFilters {
    const filters: TimeslotFilters = {
        filters: {},
        sorted: [],
        selected: null,
        update(data: ShopData, timeslotQuery: string | null = null): void {
            filters.filters = {};

            let shopFilters: null | { start: Date; end: Date } = null;

            if (
                data.filters.start &&
                /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(data.filters.start)
            ) {
                const [y, m, d] = data.filters.start
                    .split('-')
                    .map((v: string) => parseInt(v, 10));

                shopFilters = {
                    start: new Date(y, m - 1, d),
                    end: new Date(y, m - 1, d + 1),
                };
            }

            for (const eventdate of data.event_dates) {
                if (shopFilters && !datesOverlap(eventdate, shopFilters)) {
                    continue;
                }

                const filter = processItem(eventdate.start, eventdate.end);
                filter.eventdates.push(eventdate);
            }

            for (const event of data.events) {
                if (
                    !event.start ||
                    !event.end ||
                    (shopFilters &&
                        !datesOverlap(
                            { start: event.start, end: event.end },
                            shopFilters
                        ))
                ) {
                    continue;
                }

                const filter = processItem(event.start, event.end);
                filter.events.push(event);
            }

            filters.sorted = Object.values(filters.filters).sort(
                (a: TimeslotFilter, b: TimeslotFilter) =>
                    a.start.getTime() - b.start.getTime()
            );

            filters.selected = timeslotQuery
                ? filters.filters[timeslotQuery] || null
                : null;
        },
    };

    function processItem(start: Date, end: Date): TimeslotFilter {
        const key = toTimeRangeString({ start, end });

        if (filters.filters[key]) {
            return filters.filters[key];
        }

        const filter: TimeslotFilter = {
            key,
            start,
            end,
            eventdates: [],
            events: [],
        };

        filters.filters[key] = filter;

        return filter;
    }

    return Vue.observable(filters);
}

export function toTimeRangeString({
    start,
    end,
}: {
    start: Date;
    end: Date;
}): string {
    return `${toTimeString(start)}-${toTimeString(end)}`;

    function toTimeString(date: Date): string {
        return [date.getHours(), date.getMinutes(), date.getSeconds()]
            .map((v: number) => v.toFixed(0).padStart(2, '0'))
            .join('');
    }
}

export function datesOverlap(
    { start: start1, end: end1 }: { start: Date; end: Date },
    { start: start2, end: end2 }: { start: Date; end: Date }
): boolean {
    return start1 <= end2 && start2 <= end1;
}
